import {
  FormConfigItem,
  NavModel,
  SectionTemplateItemConfig,
} from '@navstation-portal/reporting/data-model/offshore-report';
import {
  FormSectionState,
  FormSectionStateArray,
  StateField,
  StateFieldType,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { NavMenuItem, NavMenuSection, NavStatus } from '@navstation-portal/shared/data-model/ui';

export function buildDynamicTemplateFromFormConfig(
  formConfigByTab: Record<string, FormConfigItem[]>,
  sectionId = 'dynamicSection'
): SectionTemplateItemConfig[] {
  return Object.entries(formConfigByTab).map(([tabId, fields]) => ({
    tabId,
    sectionId,
    navItems: (fields ?? []).map((f) => ({
      id: f.id,
      label: f.label,
      status: 'pending',
    })),
  }));
}

export function buildOffshoreNavModel<TStores extends object>(args: {
  template: SectionTemplateItemConfig[];
  stores: TStores;
}): NavModel {
  const { template, stores } = args;
  if (!template?.length) return { navSections: [], validityMap: {} };

  const storesByTab = stores as Record<string, Record<string, unknown>>;
  const childrenBySection = new Map<string, Map<string, NavMenuItem>>();
  const sectionLabelById = new Map<string, string>();
  const validityMap: Record<string, NavStatus> = {};

  for (const templateItem of template) {
    const { tabId, sectionId, label, navItems = [] } = templateItem;
    if (!sectionId) continue;
    const sectionLabel = label ?? formatFieldName(sectionId);

    if (!sectionLabelById.has(sectionId) || label) {
      sectionLabelById.set(sectionId, sectionLabel);
    }

    let sectionState;
    if (tabId && storesByTab[tabId] && typeof storesByTab[tabId][sectionId] === 'function') {
      sectionState = (storesByTab[tabId][sectionId] as () => FormSectionState | undefined)();
    } else {
      for (const tab of Object.keys(storesByTab)) {
        if (storesByTab[tab] && typeof storesByTab[tab][sectionId] === 'function') {
          sectionState = (storesByTab[tab][sectionId] as () => FormSectionState | undefined)();
          break;
        }
      }
    }

    const childrenById = childrenBySection.get(sectionId) ?? new Map<string, NavMenuItem>();
    const resolvedSectionStatus = templateItem.statusResolver?.();
    const sectionStatus: NavStatus | 'hidden' =
      resolvedSectionStatus ?? (sectionState ? formSectionStateToNavStatus(sectionState) : 'pending');

    for (const navItem of navItems) {
      const resolvedItemStatus = navItem.statusResolver?.();
      const stateKey = navItem.fieldId ?? navItem.id;
      const derivedFromState =
        sectionState && stateKey in sectionState ? fieldOrArrayToNavStatus(sectionState[stateKey]) : undefined;

      const status: NavStatus | 'hidden' = resolvedItemStatus ?? navItem.status ?? derivedFromState ?? sectionStatus;

      const isHidden = status === 'hidden';
      const displayStatus: NavStatus = isHidden ? 'pending' : (status as NavStatus);
      if (!isHidden) {
        validityMap[navItem.id] = displayStatus;
      }
      const itemLabel = navItem.label ?? formatFieldName(navItem.id);

      childrenById.set(navItem.id, {
        id: navItem.id,
        label: `${itemLabel}`,
        status: displayStatus,
        hidden: isHidden,
      });
    }

    if (childrenById.size > 0) {
      childrenBySection.set(sectionId, childrenById);
    }
  }

  const navSections: NavMenuSection[] = Array.from(childrenBySection, ([sectionId, byId]) => {
    const children = Array.from(byId.values());
    return {
      id: sectionId,
      label: sectionLabelById.get(sectionId) ?? formatFieldName(sectionId),
      status: mergeStatuses(...children.map((c) => c.status)),
      children,
    };
  });

  return { navSections, validityMap };
}

export function formatFieldName(controlName: string): string {
  return controlName.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (str) => str.toUpperCase());
}

export function mergeStatuses(...statuses: NavStatus[]): NavStatus {
  if (statuses.length === 0) return 'pending';
  if (statuses.some((s) => s === 'invalid')) return 'invalid';
  if (statuses.every((s) => s === 'valid')) return 'valid';
  return 'pending';
}

export function fieldToNavStatus(field?: StateField<StateFieldType>): NavStatus {
  if (!field) return 'pending';
  if (field.status === 'DISABLED') return 'valid';
  if (field.status === 'VALID' && field.pristine && isEmptyFieldValue(field.value)) {
    return 'pending';
  }
  if (field.status === 'VALID') return 'valid';
  if (field.status === 'INVALID') return field.touched ? 'invalid' : 'pending';

  // if (field.status === 'INVALID') return field.pristine ? 'pending' : 'invalid';
  return 'pending';
}

function isEmptyFieldValue(value: StateFieldType): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (typeof value === 'number' || typeof value === 'boolean') return false;
  if (value instanceof Date) return Number.isNaN(value.getTime());
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value !== 'object') return false;

  return Object.values(value).every((entry) => isEmptyFieldValue(entry));
}

export function tableRowsToNavStatus(rows: FormSectionStateArray): NavStatus {
  if (!rows.length) return 'pending';

  const statuses: NavStatus[] = [];
  for (const row of rows) {
    for (const value of Object.values(row)) {
      if (!value || typeof value !== 'object') continue;
      if (!('status' in value) || !('pristine' in value)) continue;
      statuses.push(fieldToNavStatus(value as StateField<StateFieldType>));
    }
  }

  return mergeStatuses(...statuses);
}

export function fieldOrArrayToNavStatus(value: FormSectionState[string] | undefined): NavStatus {
  if (!value) return 'pending';
  return Array.isArray(value)
    ? tableRowsToNavStatus(value as FormSectionStateArray)
    : fieldToNavStatus(value as StateField<StateFieldType>);
}

export function formSectionStateToNavStatus(sectionState: FormSectionState): NavStatus {
  const statuses: NavStatus[] = [];
  for (const value of Object.values(sectionState)) {
    statuses.push(fieldOrArrayToNavStatus(value));
  }
  return mergeStatuses(...statuses);
}
